import defu from 'defu';
import { get, isObject } from '@thinkvn/utils';
import Cookies from 'js-cookie';
import { ref, computed, getCurrentInstance } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import { useRouter, useRoute } from 'vue-router';

const useToken = () => {
  const key = `auth.local`;
  const getToken = () => Cookies.get(key);
  const setToken = (tokenValue) => Cookies.set(key, tokenValue);
  const removeToken = () => Cookies.remove(key);
  return { key, getToken, setToken, removeToken };
};
const useToken$1 = useToken;

const useUser = createSharedComposable((options) => {
  const { removeToken } = useToken$1();
  const { local } = options;
  const { endpoints } = local;
  const { propertyInFetch, propertyRole, propertyPermission } = local.user;
  const user = ref(null);
  const loggedIn = ref(false);
  const setUser = (data) => {
    user.value = data;
    loggedIn.value = true;
  };
  const getRole = () => {
    if (!user.value)
      return "";
    return user.value[propertyRole];
  };
  const isRole = (role) => {
    const roleUser = getRole();
    return roleUser === role;
  };
  const getPermissions = () => {
    if (!user.value)
      return [];
    return user.value[propertyPermission] || [];
  };
  const hasPermission = (permission) => {
    const permissionsUser = getPermissions();
    return permissionsUser.includes(permission);
  };
  const fetchUser = async () => {
    if (!endpoints.user)
      return;
    try {
      const { data } = await options.fetch(endpoints.user.url, {
        method: endpoints.user.method
      });
      const _user = propertyInFetch ? get(data, propertyInFetch) : data;
      setUser(_user);
      return _user;
    } catch (e) {
      resetState();
      removeToken();
      throw e;
    }
  };
  const resetState = () => {
    user.value = null;
    loggedIn.value = false;
  };
  return {
    setUser,
    getRole,
    isRole,
    getPermissions,
    hasPermission,
    fetchUser,
    resetState,
    user: computed(() => user.value),
    loggedIn: computed(() => loggedIn.value)
  };
});
const useUser$1 = useUser;

const DEFAULT_OPTION = {
  fullPathRedirect: true,
  watchLoggedIn: true,
  redirect: {
    login: "/login",
    logout: "/login",
    home: "/"
  },
  local: {
    endpoints: {
      login: { url: "/api/auth/login", method: "post" },
      logout: { url: "/api/auth/logout", method: "post" },
      user: { url: "/api/auth/user", method: "get" }
    },
    token: {
      property: "token",
      type: "Bearer",
      name: "Authorization"
    },
    user: {
      propertyInLogin: "user",
      propertyInFetch: "",
      propertyRole: "role",
      propertyPermission: "permissions",
      autoFetch: true
    }
  }
};

const createAuth = (app, _options) => {
  const options = defu(_options, DEFAULT_OPTION);
  app.config.globalProperties.authOptions = options;
  const { router, fetch, redirect, fullPathRedirect, local } = options;
  const { getToken } = useToken$1();
  const { loggedIn, fetchUser, isRole, hasPermission } = useUser$1(options);
  app.config.globalProperties.$hasPermission = hasPermission;
  app.config.globalProperties.$isRole = isRole;
  fetch.interceptors.request.use((config) => {
    if (!config)
      return;
    const token = getToken();
    if (token) {
      config.headers[DEFAULT_OPTION.local.token.name] = token;
    }
    return config;
  });
  router.beforeEach(async (to, from, next) => {
    const token = getToken();
    const { auth: metaAuth } = to.meta;
    const isValidateAuthenticatedNextPage = (metaAuth2) => {
      let isAuthenticated = false;
      if (metaAuth2 === true && loggedIn.value) {
        isAuthenticated = true;
      }
      if (isObject(metaAuth2)) {
        if (metaAuth2.hasOwnProperty("role")) {
          isAuthenticated = isRole(metaAuth2.role);
        }
        if (metaAuth2.hasOwnProperty("permission")) {
          isAuthenticated = hasPermission(metaAuth2.permission);
        }
      }
      return isAuthenticated;
    };
    const redirectToLoginPage = () => {
      const redirectPath = fullPathRedirect ? to.fullPath : to.path;
      next(`${redirect.login}?redirect=${redirectPath}`);
    };
    if (token) {
      if (to.path === redirect.login) {
        return next({ path: redirect.home });
      }
      if (!loggedIn.value && local.user.autoFetch) {
        try {
          await fetchUser();
        } catch {
          redirectToLoginPage();
        }
      }
      if (!metaAuth || isValidateAuthenticatedNextPage(metaAuth)) {
        return next();
      }
      return next(`${redirect.home}`);
    }
    if (redirect.login === to.path || !metaAuth || isValidateAuthenticatedNextPage(metaAuth)) {
      return next();
    }
    return redirectToLoginPage();
  });
};
const authPlugin = {
  install: createAuth
};
const authPlugin$1 = authPlugin;

const useProviderLocal = (options) => {
  const router = useRouter();
  const route = useRoute();
  const { setToken, removeToken } = useToken$1();
  const { setUser, fetchUser, resetState } = useUser$1(options);
  const { redirect, watchLoggedIn } = options;
  const { endpoints, token, user } = options.local;
  const redirectAfterLogin = () => {
    const path = route.query.redirect || redirect.home;
    router.replace(path);
  };
  const redirectAfterLogout = () => {
    const isNeedRedirect = route.meta.auth;
    if (isNeedRedirect) {
      const path = redirect.logout || redirect.home;
      router.replace(path);
    }
  };
  const login = async (args) => {
    if (!endpoints.login) {
      return;
    }
    const { data } = await options.fetch(endpoints.login.url, {
      method: endpoints.login.method,
      data: args
    });
    const _token = get(data, token.property);
    const _user = get(data, user.propertyInLogin);
    if (_token) {
      setToken(token.type ? token.type + " " + _token : _token);
    }
    if (_user) {
      setUser(_user);
    } else {
      await fetchUser();
    }
    redirectAfterLogin();
  };
  const logout = async (args) => {
    removeToken();
    resetState();
    if (watchLoggedIn) {
      redirectAfterLogout();
    }
    if (!endpoints.logout) {
      return;
    }
    const { data } = await options.fetch(endpoints.logout.url, {
      method: endpoints.logout.method,
      data: args
    });
    return data;
  };
  return { login, logout };
};
const useProviderLocal$1 = useProviderLocal;

const useAuth = (_options) => {
  const instance = getCurrentInstance();
  const options = _options || instance?.appContext.config.globalProperties.authOptions;
  const {
    user,
    loggedIn,
    setUser,
    fetchUser,
    getRole,
    isRole,
    getPermissions,
    hasPermission,
    resetState
  } = useUser$1(options);
  const { setToken, getToken, removeToken } = useToken$1();
  const { login, logout } = useProviderLocal$1(options);
  return {
    login,
    logout,
    setToken,
    getToken,
    removeToken,
    user,
    loggedIn,
    setUser,
    getRole,
    isRole,
    getPermissions,
    hasPermission,
    resetState,
    fetchUser
  };
};
const useAuth$1 = useAuth;

export { DEFAULT_OPTION, authPlugin$1 as authPlugin, useAuth$1 as useAuth, useToken$1 as useToken };
